import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  CardActionArea,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Stack,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import api from '../api';

export default function CareerMapperPage() {
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // profileId to delete

  useEffect(() => {
    fetchProfiles();
  }, []);

  async function fetchProfiles() {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/api/career/profiles');
      setProfiles(res.data.profiles || []);
    } catch (err) {
      setError('Failed to load profiles');
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate() {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      await api.post('/api/career/profiles', { person_name: newName.trim() });
      setCreateOpen(false);
      setNewName('');
      fetchProfiles();
    } catch (err) {
      setError('Failed to create profile');
    } finally {
      setCreating(false);
    }
  }

  async function handleDelete(id) {
    try {
      await api.delete(`/api/career/profiles/${id}`);
      setDeleteConfirm(null);
      fetchProfiles();
    } catch (err) {
      setError('Failed to delete profile');
    }
  }

  const cardSx = {
    borderRadius: 2,
    border: '1px solid',
    borderColor: 'divider',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    transition: 'box-shadow 0.2s, border-color 0.2s',
    '&:hover': {
      boxShadow: '0 4px 20px rgba(99,102,241,0.15)',
      borderColor: 'rgba(99,102,241,0.4)',
    },
  };

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
          <Box>
            <Typography variant="h5" fontWeight={700} color="text.primary">
              Career Mapper
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              Visualise career journeys parsed from LinkedIn — as interactive node graphs.
            </Typography>
          </Box>
          <Stack direction="row" gap={1.5}>
            <Button
              variant="outlined"
              startIcon={<CompareArrowsIcon />}
              onClick={() => navigate('/career/compare')}
              sx={{ borderColor: 'rgba(99,102,241,0.5)', color: '#818cf8', borderRadius: 2, fontWeight: 600, '&:hover': { borderColor: '#6366f1', bgcolor: 'rgba(99,102,241,0.06)' } }}
            >
              Compare
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateOpen(true)}
              sx={{ bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' }, borderRadius: 2, fontWeight: 600 }}
            >
              New Profile
            </Button>
          </Stack>
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>{error}</Alert>}

        {loading ? (
          <Box display="flex" justifyContent="center" pt={8}>
            <CircularProgress sx={{ color: '#6366f1' }} />
          </Box>
        ) : profiles.length === 0 ? (
          <Box
            sx={{
              textAlign: 'center',
              py: 10,
              border: '2px dashed',
              borderColor: 'divider',
              borderRadius: 3,
            }}
          >
            <AccountTreeIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" fontWeight={600}>
              No career profiles yet
            </Typography>
            <Typography variant="body2" color="text.disabled" mt={1} mb={3}>
              Create a profile for yourself or someone else, then upload LinkedIn data to generate the graph.
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setCreateOpen(true)}
              sx={{ borderColor: '#6366f1', color: '#6366f1', borderRadius: 2 }}
            >
              Create First Profile
            </Button>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {profiles.map(profile => (
              <Grid item xs={12} sm={6} md={4} key={profile.id}>
                <Card sx={cardSx}>
                  <CardActionArea onClick={() => navigate(`/career/${profile.id}`)} sx={{ p: 0 }}>
                    <CardContent sx={{ p: 3 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Box>
                          <Typography variant="h6" fontWeight={700} color="text.primary" gutterBottom>
                            {profile.person_name}
                          </Typography>
                          <Chip
                            label={`${profile.versions.length} version${profile.versions.length !== 1 ? 's' : ''}`}
                            size="small"
                            sx={{
                              bgcolor: 'rgba(99,102,241,0.1)',
                              color: '#6366f1',
                              fontWeight: 600,
                              fontSize: '0.72rem',
                            }}
                          />
                        </Box>
                        <ArrowForwardIcon sx={{ color: 'text.disabled', mt: 0.5 }} />
                      </Stack>
                      <Typography variant="caption" color="text.disabled" display="block" mt={2}>
                        Last updated {new Date(profile.updated_at).toLocaleDateString('en-SG', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                  {/* Delete button outside ActionArea */}
                  <Box sx={{ px: 2, pb: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); setDeleteConfirm(profile.id); }}
                      sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>

      {/* Create dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle fontWeight={700}>New Career Profile</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Person's Name"
            placeholder="e.g. John Doe"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleCreate()}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCreateOpen(false)} color="inherit">Cancel</Button>
          <Button
            onClick={handleCreate}
            disabled={!newName.trim() || creating}
            variant="contained"
            sx={{ bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } }}
          >
            {creating ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm dialog */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle fontWeight={700}>Delete Profile?</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">
            This will permanently delete the profile and all its versions. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteConfirm(null)} color="inherit">Cancel</Button>
          <Button onClick={() => handleDelete(deleteConfirm)} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
